import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Roomle-style floating chrome around the full-bleed viewer.
 * Docs: docs/VIEWER_UX_BRIEF.md § Roomle chrome · docs/ADU_SITE_PLANNER.md
 */

export const PLANNER_STAGES = [
  { id: 'site', label: 'Site' },
  { id: 'place', label: 'Place' },
  { id: 'inspect', label: 'Inspect' },
  { id: 'next', label: 'Next' },
];

function IconLayers() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 3 3 8l9 5 9-5-9-5Zm0 9.5L3 7.5V16l9 5 9-5V7.5l-9 5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSection() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 4h16v16H4V4Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path d="M12 4v16" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" />
    </svg>
  );
}

function IconContext() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M4.5 12h15M12 4.5c2.2 2.4 3.3 4.9 3.3 7.5S14.2 17.1 12 19.5C9.8 17.1 8.7 14.6 8.7 12S9.8 6.9 12 4.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function IconLock({ locked }) {
  return locked ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 11V8a4 4 0 1 1 8 0v3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 11V8a4 4 0 0 1 7.5-2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconSync() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4.5 12a7.5 7.5 0 0 1 12.7-5.4L19 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path d="M19 4v5h-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M19.5 12a7.5 7.5 0 0 1-12.7 5.4L5 15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path d="M5 20v-5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function IconLocalize() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12 3v3M12 18v3M3 12h3M18 12h3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function RailButton({ label, active, disabled, onClick, children }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={`flex flex-col items-center gap-0.5 w-14 py-2 rounded-[10px] border text-white transition-colors disabled:opacity-35 ${
        active
          ? 'bg-transparent border-white'
          : 'border-transparent hover:bg-white/10'
      }`}
    >
      {children}
      <span className="text-[9px] font-medium tracking-wide uppercase leading-none">
        {label}
      </span>
    </button>
  );
}

/**
 * @param {'site'|'place'|'inspect'|'next'|'tools'} stage
 */
function ViewerChrome({
  stage = 'site',
  onStageChange,
  site = null,
  structureCount = 0,
  siteContentReady = false,
  viewLocked = false,
  onViewLockedChange,
  sectionEnabled = false,
  onToggleSection,
  onOpenLayers,
  onOpenContext,
  onOpenSync,
  onAddStructure,
  onPrimaryAction,
  primaryLabel = 'Continue',
  primaryDisabled = false,
  sheet,
  sheetCollapsed = false,
  onSheetCollapsedChange,
  /** Viewport chip while catalog GLB preloads / instances resolve */
  modelLoadLabel = null,
  /** Global site boot (geocode → Photorealistic pin LODs → floor lock) */
  siteBootLoading = false,
  siteBootTitle = 'Loading site',
  siteBootDetail = null,
  onLocalize,
  children,
  toolsMenu = null,
}) {
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (!toolsRef.current?.contains(e.target)) setToolsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const addressLine = site?.formattedAddress || 'Load a site to begin';

  return (
    <div className="fixed inset-0 z-0 flex flex-col bg-[#d9d4cc] font-bai text-gray-900 overflow-hidden">
      {/* Viewport */}
      <div className="absolute inset-0 z-0">{children}</div>

      {/* Top bar — brand + Roomle-style stage pills */}
      <header className="relative z-30 flex items-center justify-between gap-3 px-3 md:px-5 pt-3 pointer-events-none">
        <Link
          to="/"
          className="pointer-events-auto shrink-0 text-sm md:text-base font-semibold tracking-tight text-gray-900 bg-white/90 backdrop-blur-sm border border-black/5 px-3 py-1.5 rounded-[10px] shadow-sm"
        >
          Construx
        </Link>

        <nav
          className="pointer-events-auto flex items-center gap-0.5 p-1 rounded-[10px] bg-white/92 backdrop-blur-md border border-black/5 shadow-sm max-w-[min(100%,28rem)] overflow-x-auto"
          aria-label="Planner stages"
        >
          {PLANNER_STAGES.map((s) => {
            const active = stage === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onStageChange?.(s.id)}
                className={`px-3 md:px-4 py-1.5 text-xs md:text-sm font-medium rounded-[10px] whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-black/[0.04]'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </nav>

        <div className="pointer-events-auto relative shrink-0" ref={toolsRef}>
          <button
            type="button"
            onClick={() => setToolsOpen((v) => !v)}
            className="text-xs font-medium tracking-wide uppercase px-3 py-1.5 rounded-[10px] bg-white/90 backdrop-blur-sm border border-black/5 shadow-sm hover:bg-white"
            aria-expanded={toolsOpen}
          >
            Tools
          </button>
          {toolsOpen && (
            <div className="absolute right-0 mt-1.5 z-40 min-w-[10.5rem] rounded-[10px] bg-white border border-black/8 shadow-lg py-1 text-sm">
              {toolsMenu}
              <button
                type="button"
                className="w-full text-left px-3 py-2 hover:bg-warm-50"
                onClick={() => {
                  setToolsOpen(false);
                  onStageChange?.('tools');
                }}
              >
                Speckle sync & more
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Left tool rail — hidden during site boot */}
      {!siteBootLoading && (
        <aside
          className="absolute z-20 left-2 md:left-4 top-1/2 -translate-y-1/2 flex flex-col gap-0.5 p-1 rounded-[10px] bg-gray-900/75 backdrop-blur-md border border-white/15 shadow-md"
          aria-label="Viewer tools"
        >
          <RailButton
            label="Layers"
            active={stage === 'inspect'}
            onClick={() => {
              onStageChange?.('inspect');
              onOpenLayers?.();
            }}
          >
            <IconLayers />
          </RailButton>
          <RailButton
            label="Section"
            active={sectionEnabled}
            onClick={() => onToggleSection?.()}
          >
            <IconSection />
          </RailButton>
          <RailButton
            label="Context"
            active={stage === 'site'}
            onClick={() => {
              onStageChange?.('site');
              onOpenContext?.();
            }}
          >
            <IconContext />
          </RailButton>
          <RailButton
            label="Localize"
            disabled={!site || !siteContentReady}
            onClick={() => onLocalize?.()}
          >
            <IconLocalize />
          </RailButton>
          <RailButton
            label={viewLocked ? 'Unlock' : 'Lock'}
            active={viewLocked}
            disabled={!site || !siteContentReady}
            onClick={() => onViewLockedChange?.(!viewLocked)}
          >
            <IconLock locked={viewLocked} />
          </RailButton>
          <RailButton
            label="Sync"
            active={stage === 'tools'}
            onClick={() => {
              onStageChange?.('tools');
              onOpenSync?.();
            }}
          >
            <IconSync />
          </RailButton>
        </aside>
      )}

      {/* Global site boot — Canvas keeps streaming tiles underneath */}
      {siteBootLoading ? (
        <div
          className="absolute inset-0 z-40 flex items-center justify-center bg-[#d9d4cc]/88 backdrop-blur-[2px]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="mx-4 w-full max-w-sm rounded-[10px] bg-white/95 border border-black/8 shadow-lg px-6 py-7 text-center">
            <span
              className="inline-block w-8 h-8 mb-4 border-[2.5px] border-gray-300 border-t-gray-900 rounded-full animate-spin"
              aria-hidden
            />
            <p className="text-[10px] uppercase tracking-[0.16em] text-gray-400 mb-1.5">
              Construx
            </p>
            <p className="text-lg font-semibold text-gray-900 mb-1">
              {siteBootTitle}
            </p>
            {siteBootDetail ? (
              <p className="text-sm text-gray-500 leading-snug">{siteBootDetail}</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Stage sheet — collapsible */}
      {!siteBootLoading && sheet ? (
        <div className="absolute z-20 top-16 md:top-[4.25rem] right-2 md:right-4 pointer-events-auto">
          {sheetCollapsed ? (
            <button
              type="button"
              onClick={() => onSheetCollapsedChange?.(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-[10px] bg-white/94 backdrop-blur-md border border-black/8 shadow-md text-xs font-medium text-gray-800 hover:bg-white"
              aria-expanded={false}
              aria-label="Expand controls"
            >
              <span>Controls</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M6 14l6-6 6 6"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : (
            <div className="relative w-[min(100vw-1rem,20.5rem)] max-h-[min(62vh,560px)]">
              <button
                type="button"
                onClick={() => onSheetCollapsedChange?.(true)}
                className="absolute z-10 top-2 right-2 w-8 h-8 flex items-center justify-center rounded-[10px] bg-white/90 border border-black/8 text-gray-600 hover:bg-warm-50 shadow-sm"
                aria-expanded
                aria-label="Collapse controls"
                title="Collapse"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M6 10l6 6 6-6"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {sheet}
            </div>
          )}
        </div>
      ) : null}

      {/* Model load chip on the map */}
      {!siteBootLoading && modelLoadLabel ? (
        <div
          className="absolute z-20 left-1/2 -translate-x-1/2 bottom-[5.5rem] md:bottom-28 pointer-events-none"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-2 px-3.5 py-2 rounded-[10px] bg-gray-900/88 text-white text-xs font-medium shadow-lg backdrop-blur-sm">
            <span
              className="inline-block w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"
              aria-hidden
            />
            <span>Loading {modelLoadLabel}…</span>
          </div>
        </div>
      ) : null}

      {/* Roomle-style + FAB */}
      {!siteBootLoading && (
        <button
          type="button"
          title="Add ADU"
          aria-label="Add ADU"
          disabled={!site || !siteContentReady}
          onClick={() => {
            onStageChange?.('place');
            onAddStructure?.();
          }}
          className="absolute z-20 left-1/2 -translate-x-1/2 bottom-[4.75rem] md:left-auto md:translate-x-0 md:right-6 md:bottom-24 w-12 h-12 rounded-[10px] bg-gray-900 text-white shadow-lg flex items-center justify-center disabled:opacity-35 hover:bg-gray-800 transition-colors"
        >
          <IconPlus />
        </button>
      )}

      {/* Bottom bar — hidden during site boot (no footer chrome while loading) */}
      {!siteBootLoading && (
        <footer className="absolute z-20 left-2 right-2 md:left-4 md:right-4 bottom-2 md:bottom-3 flex items-center gap-3 px-3 md:px-4 py-2.5 rounded-[10px] bg-white/94 backdrop-blur-md border border-black/5 shadow-md">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 leading-none mb-0.5">
              Construx site planner
            </p>
            <p className="text-sm font-medium truncate" title={addressLine}>
              {addressLine}
            </p>
          </div>
          <p className="hidden sm:block text-xs text-gray-500 tabular-nums shrink-0">
            {structureCount === 1 ? '1 ADU' : `${structureCount} ADUs`}
          </p>
          <button
            type="button"
            disabled={primaryDisabled}
            onClick={() => onPrimaryAction?.()}
            className="shrink-0 px-3.5 py-2 text-sm font-medium rounded-[10px] bg-gray-900 text-white disabled:opacity-40 hover:bg-gray-800 transition-colors"
          >
            {primaryLabel}
          </button>
        </footer>
      )}
    </div>
  );
}

export default ViewerChrome;
